import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Snackbar,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

type PaymentMethod = 'cod' | 'om' | 'wave';

const Checkout: React.FC = () => {
  const { state, clearCart } = useCart();
  const totalPrice = state.total;

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '' });

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cod');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  type FieldKey = 'firstName' | 'lastName' | 'address' | 'city' | 'postalCode' | 'phone' | 'email';
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const inputRefs: Record<FieldKey, React.RefObject<HTMLInputElement>> = {
    firstName: useRef<HTMLInputElement>(null),
    lastName: useRef<HTMLInputElement>(null),
    address: useRef<HTMLInputElement>(null),
    city: useRef<HTMLInputElement>(null),
    postalCode: useRef<HTMLInputElement>(null),
    phone: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
  };

  const validateField = (key: FieldKey, value: string): string | undefined => {
    const trimmed = value.trim();
    switch (key) {
      case 'firstName':
      case 'lastName':
      case 'address':
      case 'city':
        if (trimmed.length === 0) return 'This field is required';
        if (trimmed.length < 2) return 'Must be at least 2 characters';
        return undefined;
      case 'postalCode': {
        if (trimmed.length === 0) return 'Postal code is required';
        if (trimmed.length < 3 || trimmed.length > 10) return 'Postal code must be 3–10 characters';
        return undefined;
      }
      case 'phone': {
        if (trimmed.length === 0) return 'Phone number is required';
        const digits = trimmed.replace(/[^0-9]/g, '');
        if (digits.length < 9 || digits.length > 15) return 'Enter a valid phone number';
        return undefined;
      }
      case 'email': {
        if (trimmed.length === 0) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) return 'Enter a valid email address';
        return undefined;
      }
      default:
        return undefined;
    }
  };

  const validateAll = () => {
    const candidate: Partial<Record<FieldKey, string>> = {};
    const fields: Array<[FieldKey, string]> = [
      ['firstName', firstName],
      ['lastName', lastName],
      ['address', address],
      ['city', city],
      ['postalCode', postalCode],
      ['phone', phone],
      ['email', email],
    ];
    let firstError: FieldKey | undefined;
    for (const [k, v] of fields) {
      const msg = validateField(k, v);
      if (msg && !firstError) firstError = k;
      if (msg) candidate[k] = msg;
    }
    return { isValid: Object.keys(candidate).length === 0, firstError, nextErrors: candidate };
  };

  const handlePlaceOrder = () => {
    const { isValid, firstError, nextErrors } = validateAll();
    if (!isValid) {
      setErrors(nextErrors);
      setSnackbar({ open: true, message: 'Please fix the highlighted fields.', severity: 'error' });
      if (firstError) {
        const el = inputRefs[firstError].current;
        el?.focus();
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    let message = 'Order placed successfully!';
    if (paymentMethod === 'cod') {
      message = 'Order placed. Please prepare exact amount to pay on delivery.';
    } else if (paymentMethod === 'om') {
      message = 'Order placed. Send the total via Orange Money. We will process once payment is confirmed.';
    } else if (paymentMethod === 'wave') {
      message = 'Order placed. Send the total via Wave. We will process once payment is confirmed.';
    }

    const orderId = `TB-${Date.now()}`;
    const order = {
      id: orderId,
      createdAt: new Date().toISOString(),
      items: state.items.map((it) => ({
        id: it.id,
        name: it.name,
        price: it.price,
        image: it.image,
        quantity: it.quantity,
        lineTotal: it.price * it.quantity,
      })),
      total: totalPrice,
      paymentMethod,
      shipping: { firstName, lastName, address, city, postalCode, phone, email },
    };
    try {
      localStorage.setItem('totalbat-lastOrder', JSON.stringify(order));
    } catch {}

    setSnackbar({ open: true, message, severity: 'success' });
    clearCart();
    setTimeout(() => navigate(`/order/confirmation?orderId=${encodeURIComponent(orderId)}`), 800);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(
          theme.palette.secondary.main,
          0.06
        )})`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Checkout</Typography>
            <Chip color="primary" label={`Items: ${state.itemCount}`} variant="outlined" />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ backdropFilter: 'blur(6px)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Shipping Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, firstName: validateField('firstName', e.target.value) }))}
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        inputRef={inputRefs.firstName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, lastName: validateField('lastName', e.target.value) }))}
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                        inputRef={inputRefs.lastName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        required
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, address: validateField('address', e.target.value) }))}
                        error={Boolean(errors.address)}
                        helperText={errors.address}
                        inputRef={inputRefs.address}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, city: validateField('city', e.target.value) }))}
                        error={Boolean(errors.city)}
                        helperText={errors.city}
                        inputRef={inputRefs.city}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Postal Code"
                        required
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, postalCode: validateField('postalCode', e.target.value) }))}
                        error={Boolean(errors.postalCode)}
                        helperText={errors.postalCode}
                        inputRef={inputRefs.postalCode}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, phone: validateField('phone', e.target.value) }))}
                        error={Boolean(errors.phone)}
                        helperText={errors.phone}
                        inputRef={inputRefs.phone}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={(e) => setErrors((p) => ({ ...p, email: validateField('email', e.target.value) }))}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        inputRef={inputRefs.email}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Box sx={{ mt: 2 }} />
              <Card sx={{ backdropFilter: 'blur(6px)' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Payment</Typography>
                  <FormControl sx={{ mt: 1 }}>
                    <FormLabel>Select a payment method</FormLabel>
                    <RadioGroup
                      row
                      name="payment-method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                    >
                      <FormControlLabel value="cod" control={<Radio />} label="Arrive Paye (Cash on Delivery)" />
                      <FormControlLabel value="om" control={<Radio />} label="Orange Money" />
                      <FormControlLabel value="wave" control={<Radio />} label="Wave" />
                    </RadioGroup>
                  </FormControl>

                  {paymentMethod === 'cod' && (
                    <Alert sx={{ mt: 2 }} severity="info" variant="outlined">
                      You will pay upon delivery. Please prepare the exact amount.
                    </Alert>
                  )}

                  {paymentMethod === 'om' && (
                    <Alert sx={{ mt: 2 }} severity="warning" variant="outlined">
                      After placing your order, send {totalPrice.toLocaleString()} FCFA via Orange Money. We will
                      process your order once we confirm the payment.
                    </Alert>
                  )}

                  {paymentMethod === 'wave' && (
                    <Alert sx={{ mt: 2 }} severity="warning" variant="outlined">
                      After placing your order, send {totalPrice.toLocaleString()} FCFA via Wave. We will process your
                      order once we confirm the payment.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Order Summary</Typography>
                  <Divider sx={{ my: 2 }} />
                  {state.items.map((it) => (
                    <Box key={it.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{it.name} × {it.quantity}</Typography>
                      <Typography variant="body2">{(it.price * it.quantity).toLocaleString()} FCFA</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Payment Method</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {paymentMethod === 'cod' && 'Arrive Paye'}
                      {paymentMethod === 'om' && 'Orange Money'}
                      {paymentMethod === 'wave' && 'Wave'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6" color="primary">{totalPrice.toLocaleString()} FCFA</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handlePlaceOrder}
                    disabled={state.items.length === 0}
                  >
                    Place Order
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
        <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar({ open: false, message: '' })}>
          <Alert severity={snackbar.severity || 'success'} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Checkout;



